import React, { Component } from 'react';
import { compact, get, isArray, isEmpty, isEqual, isNull, keyBy, omit } from 'lodash';
import memoizeOne from 'memoize-one';
import PropTypes from 'prop-types';
import { PAGINATION_TYPE, TaboolaDataGrid } from 'taboola-ultimate-ui';
import withFilterIcon from 'modules/campaigns/modules/campaigns-reports/components/CellRenderers/withFilterIcon';
import {
    actionsColumnType,
    columnsTypesMap,
    defaultColumnDefinition,
} from 'modules/campaigns/modules/campaigns-reports/config';
import CustomHeader from './CustomHeader';
import CustomPaginationBar from './CustomPaginationBar';

export const BLACK_LIST = [
    'dataField',
    'valueChangeHandler',
    'csvValueGetter',
    'csv',
    'isDynamicField',
    'referenceColId',
    'permissions',
    'accountConfigurations',
    'csvValueGetterParams',
    'csvHeaderValueGetter',
    'visibilityCondition',
    'id',
    'disabled',
    'details',
];

// unit: ms
export const CELL_FLASH_DURATION = 50;
export const CELL_FADE_DURATION = 9950;
const ROW_FLASH_DURATION = 2000;

const MULTI_ROW_SELECTION = {
    mode: 'multiRow',
    checkboxes: false,
    headerCheckbox: false,
};

class ReportDataGrid extends Component {
    gridApi = null;

    handleCellValueChange = e => {
        const { onCellValueChange, context } = this.props;
        const { colDef: sanitizedColDef, data: rowData, newValue, oldValue, rowIndex, api, node } = e;
        const colDef = this.props.columnDefinition.find(col => col.colId === sanitizedColDef.colId);
        const { valueChangeHandler, field, dataField } = colDef;

        if (this.isValueChanged(newValue, oldValue) && this.isChangeValid(e)) {
            api &&
                api.flashCells({
                    rowNodes: [node],
                    flashDuration: CELL_FLASH_DURATION,
                    fadeDuration: CELL_FADE_DURATION,
                });
            onCellValueChange &&
                onCellValueChange({
                    valueChangeHandler,
                    field: dataField || field,
                    rowData,
                    newValue,
                    oldValue,
                    rowIndex,
                    context,
                });
        }
    };

    sanitizeColDefs = (colDefs, columnState) =>
        colDefs
            .filter(
                ({ isDynamicField, colId }) =>
                    !isDynamicField || (!isEmpty(columnState) && !get(columnState[colId], 'hide'))
            )
            .map(colDef => omit(colDef, BLACK_LIST));

    getColumnStateMap = memoizeOne(columnState => keyBy(columnState, 'colId'));

    sanitizeColDefsMemo = memoizeOne(this.sanitizeColDefs);

    sanitizeFooterColDefsMemo = memoizeOne(this.sanitizeColDefs);

    getDefaultColumn = memoizeOne(defaultColumnDefinitionProp => ({
        ...defaultColumnDefinition,
        ...defaultColumnDefinitionProp,
    }));

    isValueChanged = (newValue, oldValue) => !isEqual(newValue, oldValue);

    isChangeValid = ({ api, column, node, newValue, data }) => {
        const cellRenderers = api.getCellRendererInstances({ columns: [column.colId], rowNodes: [node] });

        if (!cellRenderers || !cellRenderers.length) {
            return true;
        }

        const [{ componentInstance: cellRenderer }] = cellRenderers;
        const validate =
            cellRenderer &&
            cellRenderer.childRef &&
            cellRenderer.childRef.current &&
            cellRenderer.childRef.current.validate;
        if (!validate) {
            return true;
        }
        return validate({ value: newValue, data });
    };

    normalizeWithFilterIcon = memoizeOne(columnDefs =>
        columnDefs.map(({ cellRenderer, ...rest }) => ({
            ...rest,
            cellRenderer: cellRenderer !== null ? withFilterIcon(cellRenderer) : cellRenderer,
        }))
    );

    flashGridCurrentRows() {
        const { resetFlashReportGridRows, gridRowToFlash } = this.props;

        if (!this.gridApi || !gridRowToFlash) {
            return;
        }

        const renderedNodes = this.gridApi.getRenderedNodes();
        const rowNodesToFlash = isArray(gridRowToFlash)
            ? renderedNodes.filter(({ data }) => gridRowToFlash.includes(data.id))
            : compact([renderedNodes.find(({ data }) => gridRowToFlash === data.id)]);

        if (!isEmpty(rowNodesToFlash)) {
            resetFlashReportGridRows();
            this.gridApi.flashCells({
                rowNodes: rowNodesToFlash,
                flashDuration: ROW_FLASH_DURATION,
                fadeDuration: ROW_FLASH_DURATION,
            });
        }
    }

    onGridReady = params => {
        this.props.onGridReady?.(params);
        this.gridApi = params.api;
    };

    getPaginationBarComponent = () => {
        const { isLegalDisclaimerVisible } = this.props;
        return props => <CustomPaginationBar {...props} isLegalDisclaimerVisible={isLegalDisclaimerVisible} />;
    };

    componentDidUpdate() {
        this.flashGridCurrentRows();
    }

    render() {
        const {
            reportId,
            columnDefinition,
            footerColumnDefs,
            footerReportData,
            isEmptyFooter,
            searchTerm,
            reportData,
            autoWidthColumns,
            defaultColumnDefinitions: propDefaultColumnDefinitions,
            paginationBarComponent: PaginationBarComponent = this.getPaginationBarComponent(),
            ...rest
        } = this.props;
        const { columnState } = rest;
        return (
            <TaboolaDataGrid
                {...rest}
                id={reportId}
                columnDefs={this.sanitizeColDefsMemo(columnDefinition, this.getColumnStateMap(columnState))}
                footerColumnDefs={this.sanitizeFooterColDefsMemo(footerColumnDefs, this.getColumnStateMap(columnState))}
                defaultColDef={this.getDefaultColumn(propDefaultColumnDefinitions)}
                columnTypes={columnsTypesMap}
                reportData={reportData}
                onCellValueChanged={this.handleCellValueChange}
                footerData={footerReportData}
                quickFilterText={searchTerm}
                isEmptyFooter={isEmptyFooter}
                withFooter={!searchTerm && reportData?.length !== 0 && (!isNull(footerReportData) || isEmptyFooter)}
                stickyHeader
                stickyFooter
                onGridReady={this.onGridReady}
                paginationType={PAGINATION_TYPE.SERVER}
                rowSelection={MULTI_ROW_SELECTION}
                customHeaderComponent={CustomHeader}
                normalizeFooterColDefs={this.normalizeWithFilterIcon}
                PaginationBar={PaginationBarComponent}
                autoWidthColumns={autoWidthColumns}
                suppressColumnVirtualisation
                suppressRowVirtualisation
            />
        );
    }
}

ReportDataGrid.propTypes = {
    /** The report id for the data presented in the grid */
    reportId: PropTypes.string.isRequired,
    /** The column definition for the grid's cells */
    columnDefinition: PropTypes.array.isRequired,
    /** The data presented in the grid */
    reportData: PropTypes.array,
    /** The data presented in the grid footer */
    footerReportData: PropTypes.array,
    /** The column definition for the grid's footer cells */
    footerColumnDefs: PropTypes.array,
    /** Should render an empty footer grid to allow horizontal scrolling */
    isEmptyFooter: PropTypes.bool,
    /** Cell inline edit event handler */
    onCellValueChange: PropTypes.func,
    /** Text to search in the grid */
    searchTerm: PropTypes.string,
    /** Empty/Error state overlay props */
    emptyStateData: PropTypes.shape({ header: PropTypes.node, content: PropTypes.node, className: PropTypes.string }),
    /** context to be held by the grid */
    context: PropTypes.object,
    /** Column name masks which should be auto sized disregards of passed column width */
    autoWidthColumns: PropTypes.array,
    /** Whether to show the legal disclaimer in the pagination bar */
    isLegalDisclaimerVisible: PropTypes.bool,
};

ReportDataGrid.defaultProps = {
    footerColumnDefs: [],
    isEmptyFooter: false,
    autoWidthColumns: [actionsColumnType],
    isLegalDisclaimerVisible: true,
};

export default ReportDataGrid;
